<template>
	<v-container v-if="initialized"
				 class="fill-height align-start"
				 fluid
				 @keydown.enter.prevent="handleEnterKey">
		<v-row justify="center">
			<v-col cols="12" md="10" xl="9" :style="{ 'margin-top': $vuetify.breakpoint.smAndUp ? '80px' : '20px' }">
				<component :key="index"
						   v-for="({index, component}) of availableIndexedSteps"
						   v-if="(index <= currentStepIndex)"
						   v-show="index === currentStepIndex"
						   v-bind:is="component"
						   :readonly="store.createMasterAccountRequestOperationExecuting">
				</component>
				<rmp-bottom-space :height="captchaRequired ? 182 : 126"></rmp-bottom-space>
			</v-col>
		</v-row>
		<v-row class="px-3 buttons rmp-master-account-actions" justify="center">
			<v-col offset="6" offset-lg="7" cols="6" lg="5" class="white pa-0">
				<v-row justify="center">
					<v-col cols="12" md="10" xl="9" class="d-flex flex-column pb-15 px-6 pt-5">
						<rmp-captcha v-if="captchaRequired"
									 v-model="captchaToken"
									 class="mb-4"
									 :disabled="createMasterAccountRequestOperationExecuting"
									 @keydown.enter.native.stop>
						</rmp-captcha>
						<div class="d-flex justify-end">
							<rmp-btn v-if="firstStep ? canGoBack : !finalStep "
									 class="px-8"
									 outlined
									 color="secondary"
									 :disabled="(!prevStepAvailable && !firstStep) || createMasterAccountRequestOperationExecuting"
									 @click="handlePrevStep">
								{{ tc("buttons.back") }}
							</rmp-btn>
							<v-spacer v-if="$vuetify.breakpoint.smAndUp"></v-spacer>
							<rmp-btn v-if="!finalStep"
									 class="px-8 ml-4"
									 :disabled="!nextStepAvailable"
									 :loading="createMasterAccountRequestOperationExecuting"
									 @click="handleNextStep">
								{{ tc("buttons.next") }}
							</rmp-btn>
						</div>
					</v-col>
				</v-row>
			</v-col>
		</v-row>
	</v-container>
</template>

<script setup lang="ts">
import RmpBtn from "@rmp/core/components/buttons/RmpBtn.vue";
import RmpCaptcha from "@rmp/core/components/fields/RmpCaptcha.vue";
import RmpBottomSpace from "@rmp/core/components/layouts/RmpBottomSpace.vue";
import { useI18n } from "vue-i18n-composable";
import RmpSelectCounterpartyStep from "@rmp/id/views/masterAccountAccess/steps/RmpSelectCounterpartyStep.vue";
import RmpContactInformationStep from "@rmp/id/views/masterAccountAccess/steps/RmpContactInformationStep.vue";
import RmpSignApplicationStep from "@rmp/id/views/masterAccountAccess/steps/RmpSignApplicationStep.vue";
import RmpDocumentsStep from "@rmp/id/views/masterAccountAccess/steps/RmpDocumentsStep.vue";
import RmpFinalStep from "@rmp/id/views/masterAccountAccess/steps/RmpFinalStep.vue";
import { computed } from "vue";
import { usePage } from "@rmp/core/composables/usePage";
import { MasterAccountApplicationStepOrdersEnum } from "@rmp/id/stores/masterAccountAccess/types/MasterAccountApplicationStepOrdersEnum";
import { useMasterAccountAccessStore } from "@rmp/id/stores/masterAccountAccess";
import { storeToRefs } from "pinia";
import RmpSelectBranchStep from "@rmp/id/views/masterAccountAccess/steps/RmpSelectBranchStep.vue";

const { tc } = useI18n();

const useStore = useMasterAccountAccessStore;
const store = useStore();

let {
	createMasterAccountRequestOperationExecuting,
	nextStepAvailable,
	prevStepAvailable,
	currentStepIndex,
	firstStep,
	finalStep,
	captchaRequired,
	captchaToken
} = storeToRefs(store);

const {
	handleNextStep,
	handlePrevStep
} = useStore();

const masterAccountApplicationStepsMap = new Map([
	[MasterAccountApplicationStepOrdersEnum.SELECT_COUNTERPARTY_STEP_ORDER, RmpSelectCounterpartyStep],
	[MasterAccountApplicationStepOrdersEnum.CONTACT_INFORMATION_STEP_ORDER, RmpContactInformationStep],
	[MasterAccountApplicationStepOrdersEnum.SELECT_BRANCH_STEP_ORDER, RmpSelectBranchStep],
	[MasterAccountApplicationStepOrdersEnum.SIGN_APPLICATION_STEP_ORDER, RmpSignApplicationStep],
	[MasterAccountApplicationStepOrdersEnum.DOCUMENTS_STEP_ORDER, RmpDocumentsStep],
	[MasterAccountApplicationStepOrdersEnum.FINAL_STEP_ORDER, RmpFinalStep]
]);

const availableIndexedSteps = computed(() => {
	return store.steps.map((x, i) => ({
		index: i,
		value: x,
		title: tc(`aliases.masterAccountApplicationStep.${x}`),
		component: masterAccountApplicationStepsMap.get(x)
	}));
});

const { initialized } = usePage({ useStore });

const canGoBack = computed(() => {
	return history.length > 2;
});

const handleEnterKey = (event: KeyboardEvent) => {
	if(!finalStep.value && nextStepAvailable.value) {
		handleNextStep();
	}
};
</script>

<style scoped lang="scss">
.buttons {
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
}
</style>
